import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Box,
    IconButton,
    Snackbar,
    Alert,
    Typography,
    Paper,
    Button,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    ContentCopy as CopyIcon,
    OpenInNew as OpenIcon,
    AddToHomeScreen as AddToHomeScreenIcon,
} from '@mui/icons-material';
import { generateQR } from '../services/api';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import CountdownTimer from '../components/shared/CountdownTimer';
import { copyToClipboard, getTimeRemaining } from '../utils/helpers';
import AppFooter from '../components/shared/AppFooter';

// Placeholder QR Code SVG
const PlaceholderQR = ({ onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            width: '100%',
            maxWidth: '400px',
            aspectRatio: '1',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fafafa',
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 3,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
                borderColor: 'primary.main',
                bgcolor: '#f5f5f5',
                transform: 'scale(1.02)',
            },
        }}
    >
        <Box
            sx={{
                width: '120px',
                height: '120px',
                mb: 2,
                opacity: 0.4,
            }}
        >
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="10" width="35" height="35" stroke="#666" strokeWidth="3" fill="none" />
                <rect x="55" y="10" width="35" height="35" stroke="#666" strokeWidth="3" fill="none" />
                <rect x="10" y="55" width="35" height="35" stroke="#666" strokeWidth="3" fill="none" />
                <rect x="20" y="20" width="15" height="15" fill="#666" />
                <rect x="65" y="20" width="15" height="15" fill="#666" />
                <rect x="20" y="65" width="15" height="15" fill="#666" />
                <rect x="55" y="55" width="8" height="8" fill="#666" />
                <rect x="67" y="55" width="8" height="8" fill="#666" />
                <rect x="79" y="55" width="8" height="8" fill="#666" />
                <rect x="55" y="67" width="8" height="8" fill="#666" />
                <rect x="79" y="67" width="8" height="8" fill="#666" />
                <rect x="55" y="79" width="8" height="8" fill="#666" />
                <rect x="67" y="79" width="8" height="8" fill="#666" />
                <rect x="79" y="79" width="8" height="8" fill="#666" />
            </svg>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Click để tạo QR Code
        </Typography>
    </Box>
);

const QRCodePage = () => {
    const [qrData, setQrData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [autoRefreshScheduled, setAutoRefreshScheduled] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);

    const formUrl = `${window.location.origin}/priority-register`;
    const orgId = process.env.REACT_APP_ORG_ID;

    // Listen for PWA install prompt
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    // Handle Add to Home Screen
    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            setSnackbar({
                open: true,
                message: 'Ứng dụng đã được cài đặt hoặc trình duyệt không hỗ trợ',
            });
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            setSnackbar({
                open: true,
                message: 'Đã thêm vào màn hình chính!',
            });
        }

        setDeferredPrompt(null);
    };

    const handleGenerateQR = useCallback(async (isAutoRefresh = false) => {
        setLoading(true);
        setError('');
        setAutoRefreshScheduled(false);

        try {
            const data = await generateQR(formUrl, orgId);
            setQrData(data);

            if (isAutoRefresh) {
                setSnackbar({ open: true, message: 'QR code đã được làm mới tự động' });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [formUrl, orgId]);

    // Schedule auto-refresh when QR is about to expire (10 seconds before)
    useEffect(() => {
        if (qrData && !autoRefreshScheduled) {
            const timeRemaining = getTimeRemaining(qrData.expiresAt);
            const refreshTime = timeRemaining - 10000; // 10 seconds before expiry

            if (refreshTime > 0) {
                const timer = setTimeout(() => {
                    handleGenerateQR(true);
                }, refreshTime);

                setAutoRefreshScheduled(true);

                return () => clearTimeout(timer);
            }
        }
    }, [qrData, autoRefreshScheduled, handleGenerateQR]);

    const handleQRExpire = async () => {
        // Fallback: Auto-refresh when QR expires (if 10s auto-refresh didn't work)
        await handleGenerateQR(true);
    };

    const handleCopyLink = async () => {
        try {
            await copyToClipboard(qrData.fullUrl);
            setSnackbar({ open: true, message: 'Đã sao chép link' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Không thể sao chép link' });
        }
    };

    const handleOpenLink = () => {
        window.open(qrData.fullUrl, '_blank');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
            }}
        >
            <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, sm: 5 },
                        borderRadius: 4,
                        minHeight: '600px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            color="primary"
                            sx={{ fontWeight: 600, mb: 1 }}
                        >
                            Tạo QR Đăng Ký Khách Hàng Ưu Đãi
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Mã QR sẽ tự động làm mới trước khi hết hạn
                        </Typography>
                    </Box>

                    <ErrorMessage error={error} onRetry={handleGenerateQR} />

                    {/* QR Code Display Area */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                        }}
                    >
                        {loading ? (
                            <LoadingSpinner message="Đang tạo QR code..." />
                        ) : qrData ? (
                            <>
                                {/* QR Code with Reload Button */}
                                <Box sx={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                                    <Box
                                        onClick={() => handleGenerateQR()}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease',
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                            },
                                        }}
                                    >
                                        <img
                                            src={qrData.qrCodeBase64}
                                            alt="QR Code"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '16px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            }}
                                        />
                                    </Box>

                                    {/* Reload Icon */}
                                    <IconButton
                                        onClick={() => handleGenerateQR()}
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            bgcolor: 'white',
                                            boxShadow: 2,
                                            '&:hover': {
                                                bgcolor: 'grey.100',
                                            },
                                        }}
                                    >
                                        <RefreshIcon color="primary" />
                                    </IconButton>
                                </Box>

                                {/* Copy and Open Link Buttons */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        width: '100%',
                                        maxWidth: '400px',
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<CopyIcon />}
                                        onClick={handleCopyLink}
                                    >
                                        Sao Chép Link
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<OpenIcon />}
                                        onClick={handleOpenLink}
                                    >
                                        Mở Link
                                    </Button>
                                </Box>

                                {/* Countdown Timer */}
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        bgcolor: 'grey.50',
                                        borderRadius: 2,
                                        width: '100%',
                                        maxWidth: '400px',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Thời gian còn lại:
                                    </Typography>
                                    <CountdownTimer expiresAt={qrData.expiresAt} onExpire={handleQRExpire} />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        Tự động làm mới trước 10 giây
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <PlaceholderQR onClick={() => handleGenerateQR()} />
                        )}
                    </Box>
                </Paper>

                {/* Add to Home Screen Button - Always visible, outside Paper */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AddToHomeScreenIcon />}
                        onClick={handleInstallClick}
                        sx={{ maxWidth: '400px', width: '100%' }}
                    >
                        Thêm vào Màn hình
                    </Button>
                </Box>
            </Container>

            <AppFooter />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default QRCodePage;
